/**
 * Describing a draft conflict as someone else's change (requirement 13.6, A4).
 *
 * When a save is refused because the draft moved, the designer needs to know
 * what the other change was before choosing between the two. Merging is ruled
 * out (A4): two physical designs do not combine element by element. So this
 * only describes: which elements the other save touched, relative to what this
 * session last saw from the server.
 */
import { regionApplications, type DesignDocument } from "../design/document";

export interface ElementChanges {
  added: string[];
  removed: string[];
  changed: string[];
}

function byId(document: DesignDocument): Map<string, string> {
  const elements = [
    ...(document.nodes ?? []),
    ...(document.edges ?? []),
    ...(document.group_instances ?? []),
    ...regionApplications(document),
    ...(document.text_notes ?? []),
  ];
  // Serialised for comparison: documents are plain JSON, and key order is
  // stable for a document that went through the same serialiser.
  return new Map(elements.map((element) => [element.local_id, JSON.stringify(element)]));
}

/** What changed from `before` to `after`, by element identifier. */
export function elementChanges(before: DesignDocument, after: DesignDocument): ElementChanges {
  const was = byId(before);
  const now = byId(after);
  const added = [...now.keys()].filter((id) => !was.has(id)).sort();
  const removed = [...was.keys()].filter((id) => !now.has(id)).sort();
  const changed = [...now.keys()].filter((id) => was.has(id) && was.get(id) !== now.get(id)).sort();
  return { added, removed, changed };
}

export function describeChanges(changes: ElementChanges): string {
  const parts: string[] = [];
  if (changes.added.length) parts.push(`added ${changes.added.join(", ")}`);
  if (changes.removed.length) parts.push(`removed ${changes.removed.join(", ")}`);
  if (changes.changed.length) parts.push(`changed ${changes.changed.join(", ")}`);
  return parts.length ? parts.join("; ") : "no element changes (design notes or other settings only)";
}
